<template>
    <div class="modal fade" tabindex="-1" aria-labelledby="taxModalLabel" :id="id">
        <div class="modal-dialog modal-dialog-centered modal-xl">
            <div class="modal-content border-0 shadow-lg">
                <!-- ヘッダー -->
                <div class="modal-header border-0 bg-gradient-primary">
                    <h5 class="modal-title fw-semibold d-flex align-items-center gap-2">
                        <span v-if="receipt.id">
                            <i class="fa-solid fa-file-pen"></i>
                            領収書編集
                        </span>
                        <span v-else>
                            <i class="fa-solid fa-plus"></i>
                            領収書新規作成
                        </span>
                    </h5>
                    <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>

                <!-- ボディ -->
                <div class="modal-body p-4" v-if="receipt_image">
                    <div class="row g-4">
                        <!-- 左カラム: 画像プレビュー -->
                        <div class="col-lg-5">
                            <div class="image-preview-card">
                                <div class="sticky">
                                    <div class="image-wrapper">
                                        <preview-file :receipt_image="receipt_image" />
                                    </div>

                                    <uploader
                                        url="/api/tools/receipt-image"
                                        :multiple="false"
                                        :max-filesize="$store.state.max_filesize"
                                        field-name="images[]"
                                        :id="receipt_image.id"
                                        @uploaded="uploaded()"
                                    />
                                </div>
                            </div>
                        </div>

                        <!-- 右カラム: フォーム -->
                        <div class="col-lg-7">
                            <!-- 基本情報セクション -->
                            <div class="form-section">
                                <h6 class="section-title">
                                    <i class="fa-solid fa-circle-info me-2"></i>
                                    基本情報
                                </h6>

                                <div class="row g-3">
                                    <div class="col-md-6">
                                        <label class="form-label">
                                            購入日付<span class="text-danger ms-1">*</span>
                                        </label>
                                        <single-picker
                                            v-model="receipt.purchase_date"
                                            placeholder="日付を選択"
                                            :max-date="new Date()"
                                            :week-start="0"
                                            :class="{'is-invalid': errors.purchase_date}"
                                            data-error-key="purchase_date"
                                        />
                                        <div v-if="errors.purchase_date" class="invalid-feedback d-block">
                                            {{ errors.purchase_date }}
                                        </div>
                                    </div>

                                    <div class="col-md-6">
                                        <label class="form-label">
                                            取引先<span class="text-danger ms-1">*</span>
                                        </label>
                                        <dropdown-selector
                                            v-model="receipt.client"
                                            placeholder="取引先を選択"
                                            label="label"
                                            :class="{'is-invalid': errors.client_id}"
                                            :options="clients"
                                        ></dropdown-selector>
                                        <div v-if="errors.client_id" class="invalid-feedback d-block">
                                            {{ errors.client_id }}
                                        </div>
                                    </div>

                                    <div class="col-12">
                                        <label class="form-label">
                                            タグ
                                            <span class="text-danger ms-1">*</span>
                                        </label>
                                        <dropdown-selector
                                            v-model="receipt.tag"
                                            placeholder="タグを選択（任意）"
                                            label="name"
                                            :class="{'is-invalid': errors.tag_id}"
                                            :options="tags"
                                        ></dropdown-selector>
                                    </div>
                                </div>
                            </div>

                            <!-- 明細セクション -->
                            <div class="form-section my-3">
                                <div class="d-flex align-items-center justify-content-between mb-3">
                                    <div class="d-flex justify-content-between align-items-center flex-grow-1 me-3">
                                        <h6 class="section-title flex-grow-1 mb-0 me-3">
                                            <i class="fa-solid fa-list me-2"></i>明細
                                        </h6>
                                        <div class="amount-total fs-5">
                                            明細合計:
                                            {{ amountTotal }} 円
                                        </div>
                                    </div>
                                    <div>
                                        <button class="btn btn-sm btn-outline-primary" @click="addDetail" tabindex="-1">
                                            <i class="fa-solid fa-plus me-1"></i>明細追加
                                        </button>
                                    </div>
                                </div>

                                <div class="details-container">
                                    <div v-for="(d, i) in receipt.receipt_details" :key="i" class="detail-card">
                                        <div class="detail-header">
                                            <span class="detail-number">明細 {{ i + 1 }}</span>
                                            <button class="btn btn-sm btn-outline-danger" @click="removeDetail(i)" tabindex="-1" :disabled="receipt.receipt_details.length == 1 && i == 0">
                                                <i class="fa-solid fa-xmark"></i>
                                                抹消
                                            </button>
                                        </div>

                                        <div class="row g-3">
                                            <div class="col-md-6">
                                                <label class="form-label">
                                                    勘定科目
                                                    <span class="text-danger ms-1">*</span>
                                                </label>
                                                <dropdown-selector
                                                    v-model="d.account_title"
                                                    placeholder="勘定科目を選択"
                                                    label="label"
                                                    :large="true"
                                                    :class="{'is-invalid': errors[`account_title_id_${i}`]}"
                                                    :options="account_titles"
                                                ></dropdown-selector>
                                                <div v-if="errors[`account_title_id_${i}`]" class="invalid-feedback d-block">
                                                    {{ errors[`account_title_id_${i}`] }}
                                                </div>
                                            </div>

                                            <div class="col-md-6">
                                                <label class="form-label">
                                                    金額
                                                    <span class="text-danger ms-1">*</span>
                                                </label>
                                                <div class="input-group input-group-lg">
                                                    <span class="input-group-text">¥</span>
                                                    <input
                                                        type="number"
                                                        v-model="d.amount"
                                                        class="form-control form-control-lg"
                                                        :class="{'is-invalid': errors[`amount_${i}`]}"
                                                        placeholder="0"
                                                        @change="calcTax(d)"
                                                    />
                                                </div>
                                                <div v-if="errors[`amount_${i}`]" class="invalid-feedback d-block">
                                                    {{ errors[`amount_${i}`] }}
                                                </div>
                                            </div>

                                            <div class="col-md-4">
                                                <label class="form-label">
                                                    消費税率
                                                    <span class="text-danger ms-1">*</span>
                                                </label>
                                                <select
                                                    v-model="d.tax_rate"
                                                    class="form-select"
                                                    @change="calcTax(d)"
                                                    :class="{'is-invalid': errors[`tax_rate_${i}`]}"
                                                >
                                                    <option :value="10">10%</option>
                                                    <option :value="8">8%</option>
                                                    <option :value="0">0%</option>
                                                </select>
                                                <div v-if="errors[`tax_rate_${i}`]" class="invalid-feedback d-block">
                                                    {{ errors[`tax_rate_${i}`] }}
                                                </div>
                                            </div>

                                            <div class="col-md-8">
                                                <label class="form-label">
                                                    消費税
                                                    <span class="text-danger ms-1">*</span>
                                                </label>
                                                <div class="input-group">
                                                    <span class="input-group-text">¥</span>
                                                    <input
                                                        type="number"
                                                        v-model="d.tax"
                                                        class="form-control"
                                                        :class="{'is-invalid': errors[`tax_${i}`]}"
                                                        placeholder="0"
                                                    />
                                                    <button
                                                        type="button"
                                                        class="btn btn-outline-secondary fw-bold"
                                                        tabindex="-1"
                                                        @click="setTaxInclusive(d)">
                                                        税込み
                                                    </button>
                                                    <button
                                                        type="button"
                                                        class="btn btn-outline-secondary"
                                                        tabindex="-1"
                                                        @click="setTaxExclusive(d)">
                                                        税抜き
                                                    </button>
                                                </div>
                                                <div v-if="errors[`tax_${i}`]" class="invalid-feedback d-block">
                                                    {{ errors[`tax_${i}`] }}
                                                </div>
                                            </div>

                                            <div class="col-12">
                                                <label class="form-label">
                                                    税区分
                                                    <span class="text-danger ms-1">*</span>
                                                </label>
                                                <dropdown-selector
                                                    v-model="d.tax_category"
                                                    placeholder="税区分を選択"
                                                    label="label"
                                                    :class="{'is-invalid': errors[`tax_category_id_${i}`]}"
                                                    :options="tax_categories"
                                                ></dropdown-selector>
                                                <div v-if="errors[`tax_category_id_${i}`]" class="invalid-feedback d-block">
                                                    {{ errors[`tax_category_id_${i}`] }}
                                                </div>
                                            </div>

                                            <div class="col-md-6">
                                                <label class="form-label text-muted">摘要（任意）</label>
                                                <input
                                                    type="text"
                                                    v-model="d.description"
                                                    class="form-control"
                                                    placeholder="摘要を入力"
                                                />
                                            </div>

                                            <div class="col-md-6">
                                                <label class="form-label text-muted">TKC伝票番号（任意）</label>
                                                <input
                                                    type="number"
                                                    v-model="d.tkc_voucher_number"
                                                    class="form-control"
                                                    placeholder="伝票番号"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <!-- 添付ファイルセクション -->
                            <div class="form-section">
                                <div v-for="(v, i) in receipt.receipt_images" class="d-flex justify-content-between file-list mb-3">
                                    <div>
                                        <preview-file :receipt_image="v" :receipt="receipt" :is_icon="true" />
                                    </div>
                                    <div>
                                        <button class="btn btn-link text-danger fw-bold btn-sm" @click="removeImage(i)">
                                            <i class="fa-solid fa-xmark me-2"></i>
                                        </button>
                                    </div>
                                </div>
                                <div v-for="v in receipt.attachments" class="mb-3">
                                    <file-view :imageFile="v" @remove="removeAttachment($event)" />
                                </div>
                                <file-store
                                    @add="addAttachment($event)" />
                            </div>
                        </div>
                    </div>
                </div>

                <!-- フッター -->
                <div class="modal-footer border-0 bg-light">
                    <button type="button" class="btn btn-secondary" @click="close()" tabindex="-1">
                        <i class="fa-solid fa-xmark me-2"></i>
                        キャンセル
                    </button>
                    <button type="button" class="btn btn-primary px-4" @click="save()" :disabled="isLoading">
                        <i class="fa-solid fa-floppy-disk me-2"></i>
                        保存
                    </button>
                </div>
            </div>
        </div>
        <loading
            v-model:active="isLoading"
            :can-cancel="false"
            :is-full-page="true"></loading>
    </div>
</template>

<style scoped>
/* モーダル全体 */
.modal-xl {
    max-width: 1440px;
}

.modal-content {
    border-radius: 12px;
}

/* ヘッダー */
.bg-gradient-primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    padding: 1.25rem 1.5rem;
}

.modal-title {
    font-size: 1.25rem;
    letter-spacing: 0.01em;
}

/* 画像プレビュー */
.image-preview-card {
    background: #f8f9fa;
    border-radius: 12px;
    padding: 1.25rem;
    height: 100%;
    display: flex;
    flex-direction: column;
}

.image-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;
    border-radius: 8px;
    padding: 1rem;
    margin-bottom: 1rem;
    height: 480px;
    min-height: 0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.img-receipt {
    max-width: 100%;
    max-height: 60vh;
    object-fit: contain;
    border-radius: 4px;
    transition: transform 0.2s;
}

.img-receipt:hover {
    transform: scale(1.02);
}


/* フォームセクション */
.form-section {
    background: #ffffff;
    border-radius: 10px;
    padding: 1.5rem;
    border: 1px solid #e9ecef;
}

.section-title {
    font-size: 1rem;
    font-weight: 600;
    color: #495057;
    margin-bottom: 1rem;
    padding-bottom: 0.75rem;
    border-bottom: 2px solid #e9ecef;
}

.form-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #495057;
    margin-bottom: 0.5rem;
}

/* 明細カード */
.details-container {
    padding-right: 0.5rem;
}

.details-container::-webkit-scrollbar {
    width: 6px;
}

.details-container::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
}

.details-container::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 10px;
}

.details-container::-webkit-scrollbar-thumb:hover {
    background: #a0aec0;
}

.detail-card {
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 10px;
    padding: 1.25rem;
    margin-bottom: 1rem;
    transition: all 0.2s;
}

.detail-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border-color: #dee2e6;
}

.detail-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid #dee2e6;
}

.detail-number {
    font-weight: 600;
    color: #667eea;
    font-size: 0.9rem;
}

/* フォーム要素 */
.form-control,
.form-select {
    border-radius: 6px;
    border: 1px solid #ced4da;
    font-size: 0.9rem;
    transition: all 0.2s;
}

.form-control:focus,
.form-select:focus {
    border-color: #667eea;
    box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.15);
}

.input-group-text {
    background-color: #f8f9fa;
    border: 1px solid #ced4da;
    border-radius: 6px 0 0 6px;
    font-weight: 500;
    color: #6c757d;
}

/* ボタン */
.btn {
    border-radius: 6px;
    font-weight: 500;
    transition: all 0.2s;
    font-size: 0.9rem;
}

.btn-primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
}

.btn-primary:hover {
    background: linear-gradient(135deg, #5568d3 0%, #65408b 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.btn-outline-primary {
    border-color: #667eea;
    color: #667eea;
}

.btn-outline-primary:hover {
    background-color: #667eea;
    border-color: #667eea;
}

.btn-outline-danger {
    border-color: #dc3545;
    color: #dc3545;
}

.btn-outline-danger:hover {
    background-color: #dc3545;
    border-color: #dc3545;
}
.file-list {
    border-bottom: 1px dashed #ddd;
    padding: .25rem;
}
/* フッター */
.modal-footer {
    padding: 1rem 1.5rem;
}
.amount-total {
    font-weight: bold;
    color: #667eea
}
/* レスポンシブ調整 */
@media (max-width: 991px) {
    .image-wrapper {
        max-height: 40vh;
    }
}
</style>

<script>
import dropdownSelector from "components/dropdownSelector.vue";
import uploader from 'components/uploader.vue';
import fileStore from 'components/fileStore.vue';
import previewFile from 'components/previewFile.vue';
import fileView from 'components/fileView.vue'
export default {
    components: {
        dropdownSelector,
        uploader,
        fileStore,
        previewFile,
        fileView,
    },
    props: {
        id: {
            type: String,
            default: 'receipt-editor'
        },
        receipt_image_id: { type: Number, default: null }
    },
    data() {
        return {
            receipt_image: {},
            receipt: {},
            account_titles: [],
            clients: [],
            partner_accounts: [],
            tags: [],
            tax_categories: [],
            errors: {},
            isLoading: false,
        }
    },
    methods: {
        init() {
            axios.get('/api/master/dropdowns').then(res => {
                this.account_titles = res.data.account_titles;
                this.clients = res.data.clients;
                this.partner_accounts = res.data.partner_accounts;
                this.tags = res.data.tags;
                this.tax_categories = res.data.tax_categories;
            }).catch(err => {Alert.err(err)});
        },
        close() {
            this.$emit('close', this.id);
        },
        uploaded() {
            this.reload();
            this.$emit("saved");
        },
        save() {
            if (this.isLoading) return;
            this.isLoading = true;
            const form_data = new FormData();
            this.appendFormDataRecursive(form_data, this.receipt, '');
            axios.post("/api/tools/receipt", form_data).then(res => {
                Alert.msg("保存しました。")
                this.$emit('saved');
                this.close();
                this.isLoading = false;
            }).catch(err => {Alert.err(err)});
        },
        // form_dataにオブジェクトを再帰的に展開して追加する
        appendFormDataRecursive(form_data, value_data, parent_key) {
            const self = this;
            if (value_data === null || value_data === undefined) {
                form_data.append(parent_key, '');
                return;
            }

            if (value_data instanceof File) {
                form_data.append(parent_key, value_data);
                return;
            }

            if (Array.isArray(value_data)) {
                value_data.forEach(function (item_value, index_no) {
                    const current_key = parent_key + '[' + index_no + ']';
                    self.appendFormDataRecursive(form_data, item_value, current_key);
                });
                return;
            }

            if (typeof value_data === 'object') {
                Object.keys(value_data).forEach(function (key_name) {
                    const current_key = parent_key ? parent_key + '[' + key_name + ']' : key_name;
                    self.appendFormDataRecursive(form_data, value_data[key_name], current_key);
                });
                return;
            }

            form_data.append(parent_key, value_data);
        },
        reload() {
            axios.get(`/api/tools/receipt-image?id=${this.receipt_image_id}`).then(res => {
                this.receipt_image = res.data;
                if (res.data.receipt) {
                    res.data.receipt.attachments = [];
                    this.receipt = res.data.receipt;
                } else {
                    this.receipt = {
                        receipt_image_id: this.receipt_image.id,
                        receipt_details: [
                        ],
                        attachments: [],
                    };
                    this.addDetail();
                }
            }).catch(err => {Alert.err(err)});
        },
        removeDetail(i) {
            this.receipt.receipt_details.splice(i, 1)
        },
        addDetail() {
            this.receipt.receipt_details.push({
                receipt_id: this.receipt.id || null,
                account_title: null,
                amount: null,
                tax: 0,
                tax_rate: 10,
                tax_category: null,
                description: '',
                tkc_voucher_number: ''
            });
        },
        setTaxExclusive(detail) {
            const rate = Number(detail.tax_rate);
            if (!detail.amount || rate === 0) {
                detail.tax = 0;
                return;
            }
            detail.tax = Math.round(detail.amount * rate / 100);
        },
        setTaxInclusive(detail) {
            const rate = Number(detail.tax_rate);
            if (!detail.amount || rate === 0) {
                detail.tax = 0;
                return;
            }
            detail.tax = Math.round(detail.amount * rate / (100 + rate));
        },
        calcTax(detail) {
            if (!detail.amount || detail.tax_rate === 0) {
                detail.tax = 0;
                return;
            }
            if (!detail.tax) {
                this.setTaxInclusive(detail);
            }
        },
        removeImage(i) {
            this.receipt.receipt_images.splice(i, 1);
        },
        addAttachment(files) {
            for (const file of files) {
                this.receipt.attachments.push(file);
            }
        },
        removeAttachment(file) {
            const attachment_index = this.receipt.attachments.findIndex((attachment_file) => attachment_file === file);

            if (attachment_index !== -1) {
                this.receipt.attachments.splice(attachment_index, 1);
            }
        }
    },
    computed: {
        amountTotal() {
            let amountTotal = 0;
            if (this.receipt?.receipt_details) {
                for (const detail of this.receipt.receipt_details) {
                    amountTotal += detail.amount;
                }
            }
            return amountTotal.toLocaleString();
        },
    },
    mounted() {
        this.init();
        $(`#${this.id}`).on("show.bs.modal", (e) => {
            this.reload();
        });
        $(`#${this.id}`).on("hidden.bs.modal", (e) => {
        });
    },
}
</script>